const ACCESS_TOKEN_KEYS = ["authKey", "access_token"];
const REFRESH_TOKEN_KEYS = ["refresh_token"];

const AUTH_STORAGE_KEYS = [
  "authKey",
  "access_token",
  "refresh_token",
  "auth_user",
  "user_id",
  "email",
  "role_name",
  "company_id",
  "name",
  "permissions",
  "user_permissions",
];

// Page-state snapshots are useful while one user navigates around the app, but
// they must never survive an account boundary in the same browser tab.
const USER_SCOPED_SESSION_STORAGE_KEYS = [
  "all_flanges_list_state_v1",
  "equipment_list_state_v1",
];

export const getAuthToken = () => {
  for (const key of ACCESS_TOKEN_KEYS) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token) return token;
  }
  return null;
};

export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;

  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
};

export const isTokenExpired = (token) => {
  if (!token) return true;

  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload !== "object") return true;

  if (typeof payload.exp !== "number") {
    return false;
  }

  return payload.exp * 1000 <= Date.now();
};

export const getRefreshToken = () => {
  for (const key of REFRESH_TOKEN_KEYS) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token) return token;
  }
  return null;
};

const permissionValues = () => {
  const storedValues = [
    localStorage.getItem("permissions"),
    localStorage.getItem("user_permissions"),
    localStorage.getItem("auth_user"),
  ];

  return [decodeJwtPayload(getAuthToken()), ...storedValues.map((value) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  })];
};

export const hasPermission = (requiredPermission) => {
  const expected = String(requiredPermission || "").trim().toLowerCase();
  if (!expected) return false;

  const includesPermission = (value) => {
    if (!value) return false;
    if (typeof value === "string") {
      return value.split(/[\s,]+/).some(
        (permission) => permission.trim().toLowerCase() === expected,
      );
    }
    if (Array.isArray(value)) return value.some(includesPermission);
    if (typeof value === "object") {
      if (value[requiredPermission] === true || value[expected] === true) return true;
      return ["permissions", "user_permissions", "permission_names", "scopes"]
        .some((key) => includesPermission(value[key]));
    }
    return false;
  };

  return permissionValues().some(includesPermission);
};

export const hasRoleOrPermission = (roles = [], permission = "") => {
  const userRole = (localStorage.getItem("role_name") || "").trim().toLowerCase();
  const allowedRoles = roles.map((role) => String(role).trim().toLowerCase());
  return allowedRoles.includes(userRole) || hasPermission(permission);
};

// True when a live access token is present. Use only when a call needs a
// currently-valid access token; for route/session gating prefer hasActiveSession().
export const isAuthenticated = () => {
  const token = getAuthToken();
  return Boolean(token) && !isTokenExpired(token);
};

// A session is active as long as we hold a non-expired refresh token. The
// access token may already be expired — axios transparently renews it via the
// /api/refresh interceptor, so an expired access token must NOT log the user out.
export const hasActiveSession = () => {
  const refreshToken = getRefreshToken();
  return Boolean(refreshToken) && !isTokenExpired(refreshToken);
};

export const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  USER_SCOPED_SESSION_STORAGE_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

export const redirectToLogin = () => {
  if (window.location.pathname.toLowerCase() === "/login") return;
  window.location.replace("/login");
};
